<div class="pm-shell">
  <div>
    <h1>Kanban</h1>
    <p class="subtitle">Issue boards with columns, WIP limits, swimlanes, quick filters, and drag-and-drop.</p>
  </div>

  @if (error()) {
    <div class="error-banner">
      <span>{{ error() }}</span>
      <button mat-icon-button type="button" (click)="error.set(null)"><mat-icon>close</mat-icon></button>
    </div>
  }

  @if (liveHint()) {
    <div class="live-banner">
      <mat-icon>sensors</mat-icon>
      <span>{{ liveHint() }}</span>
      <button mat-icon-button type="button" (click)="liveHint.set(null)"><mat-icon>close</mat-icon></button>
    </div>
  }

  <div class="pm-toolbar">
    <mat-form-field appearance="outline">
      <mat-label>Project</mat-label>
      <mat-select [value]="selectedProjectId()" (selectionChange)="selectProject($event.value)">
        @for (p of projects(); track p.id) {
          <mat-option [value]="p.id">{{ p.name }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>Board</mat-label>
      <mat-select [value]="selectedBoardId()" (selectionChange)="selectBoard($event.value)">
        @for (b of boards(); track b.id) {
          <mat-option [value]="b.id">{{ b.name }}{{ b.isDefault ? ' ★' : '' }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>Swimlanes</mat-label>
      <mat-select [(ngModel)]="swimlaneType" (ngModelChange)="updateSwimlane()">
        @for (s of swimlaneOptions; track s.value) {
          <mat-option [value]="s.value">{{ s.label }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <button mat-stroked-button type="button" (click)="refreshBoard()">
      <mat-icon>refresh</mat-icon> Refresh
    </button>

    <button mat-stroked-button type="button" [class.active-filter]="selectionMode()" (click)="toggleSelectionMode()">
      <mat-icon>{{ selectionMode() ? 'check_box' : 'check_box_outline_blank' }}</mat-icon>
      Select
    </button>

    @if (loading()) {
      <mat-spinner diameter="28"></mat-spinner>
    }
  </div>

  <div class="filters-row">
    <mat-form-field appearance="outline" class="grow">
      <mat-label>Quick filter search</mat-label>
      <input matInput [(ngModel)]="filterSearch" (keyup.enter)="refreshBoard()" placeholder="Key or summary" />
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>Saved filter</mat-label>
      <mat-select [(ngModel)]="savedFilterId" (ngModelChange)="refreshBoard()">
        <mat-option value="">None</mat-option>
        @for (f of savedFilters(); track f.id) {
          <mat-option [value]="f.id">{{ f.name }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
    <button mat-stroked-button type="button" (click)="saveBoardDefaultFilter()" matTooltip="Save current saved filter as board default">
      <mat-icon>bookmark</mat-icon> Set default
    </button>

    <mat-form-field appearance="outline">
      <mat-label>Assignee</mat-label>
      <mat-select [(ngModel)]="filterAssigneeId" (ngModelChange)="refreshBoard()">
        <mat-option value="">Anyone</mat-option>
        @for (a of board()?.filterOptions?.assignees || []; track a.value) {
          <mat-option [value]="a.value">{{ a.label }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>Epic</mat-label>
      <mat-select [(ngModel)]="filterEpicId" (ngModelChange)="refreshBoard()">
        <mat-option value="">Any</mat-option>
        @for (e of board()?.filterOptions?.epics || []; track e.value) {
          <mat-option [value]="e.value">{{ e.label }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>Type</mat-label>
      <mat-select [(ngModel)]="filterType" (ngModelChange)="refreshBoard()">
        <mat-option value="">Any</mat-option>
        @for (t of board()?.filterOptions?.types || []; track t.value) {
          <mat-option [value]="t.value">{{ t.label }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>Priority</mat-label>
      <mat-select [(ngModel)]="filterPriority" (ngModelChange)="refreshBoard()">
        <mat-option value="">Any</mat-option>
        @for (p of board()?.filterOptions?.priorities || []; track p.value) {
          <mat-option [value]="p.value">{{ p.label }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>Label</mat-label>
      <mat-select [(ngModel)]="filterLabelId" (ngModelChange)="refreshBoard()">
        <mat-option value="">Any</mat-option>
        @for (l of board()?.filterOptions?.labels || []; track l.value) {
          <mat-option [value]="l.value">{{ l.label }}</mat-option>
        }
      </mat-select>
    </mat-form-field>

    <mat-checkbox [(ngModel)]="filterUnassigned" (ngModelChange)="refreshBoard()">Unassigned</mat-checkbox>
    <button mat-button type="button" (click)="clearFilters()">Clear</button>
  </div>

  @if (selectionMode() && selectedCount() > 0) {
    <div class="bulk-bar">
      <span>{{ selectedCount() }} selected</span>
      <mat-form-field appearance="outline">
        <mat-label>Assign to</mat-label>
        <mat-select [(ngModel)]="bulkAssigneeId">
          <mat-option value="">Unassigned</mat-option>
          @for (a of board()?.filterOptions?.assignees || []; track a.value) {
            <mat-option [value]="a.value">{{ a.label }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      <button mat-stroked-button type="button" (click)="bulkAssign()">
        <mat-icon>person</mat-icon> Bulk assign
      </button>
      <mat-form-field appearance="outline">
        <mat-label>Move to column</mat-label>
        <mat-select [(ngModel)]="bulkTargetColumnId">
          @for (col of columns(); track col.id) {
            <mat-option [value]="col.id">{{ col.name }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
      <button mat-stroked-button type="button" [disabled]="!bulkTargetColumnId" (click)="bulkMove()">
        <mat-icon>drive_file_move</mat-icon> Bulk move
      </button>
      <button mat-button type="button" (click)="clearSelection()">Clear selection</button>
    </div>
  }

  <div class="setup-row">
    <mat-form-field appearance="outline">
      <mat-label>New board</mat-label>
      <input matInput [(ngModel)]="newBoardName" placeholder="Main board" />
    </mat-form-field>
    <button mat-flat-button color="primary" type="button" (click)="createBoard()">
      <mat-icon>dashboard_customize</mat-icon> Create board
    </button>

    <mat-form-field appearance="outline">
      <mat-label>New column</mat-label>
      <input matInput [(ngModel)]="newColumnName" />
    </mat-form-field>
    <mat-form-field appearance="outline">
      <mat-label>Status</mat-label>
      <mat-select [(ngModel)]="newColumnStatus">
        @for (s of statusOptions; track s.value) {
          <mat-option [value]="s.value">{{ s.label }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
    <mat-form-field appearance="outline" class="wip-field">
      <mat-label>WIP limit</mat-label>
      <input matInput type="number" [(ngModel)]="newColumnWip" min="1" />
    </mat-form-field>
    <button mat-stroked-button type="button" [disabled]="!selectedBoardId()" (click)="addColumn()">
      <mat-icon>view_column</mat-icon> Add column
    </button>
  </div>

  @if (!selectedBoardId()) {
    <p class="empty">Create a Kanban board for this project to get started.</p>
  } @else {
    <div class="board-scroll" cdkDropListGroup>
      @for (col of columns(); track col.id) {
        <div class="board-column" [class.wip-exceeded]="col.isWipExceeded">
          <h3>
            <span>{{ col.name }}</span>
            <span class="status-label muted">{{ col.mappedIssueStatusLabel || '' }}</span>
            <span class="count" [class.warn]="col.isWipExceeded">
              {{ col.issueCount }}@if (col.wipLimit) {/{{ col.wipLimit }}}
            </span>
          </h3>
          <div class="wip-edit">
            <mat-form-field appearance="outline" class="wip-field">
              <mat-label>WIP</mat-label>
              <input matInput type="number" [(ngModel)]="col.wipLimit" min="1" (change)="saveColumnWip(col)" />
            </mat-form-field>
          </div>

          @for (lane of col.swimlanes; track lane.key) {
            @if (board()?.swimlaneType !== 0) {
              <div class="swimlane-label">{{ lane.label }}</div>
            }
            <div
              class="drop-list"
              cdkDropList
              [id]="dropId(col.id, lane.key)"
              [cdkDropListData]="lane.issues"
              [cdkDropListConnectedTo]="connectedLists()"
              (cdkDropListDropped)="drop($event, col)">
              @for (issue of lane.issues; track issue.id) {
                <div class="task-card" [class.overdue]="issue.isOverdue" [class.selected]="isSelected(issue.id)" cdkDrag (click)="openIssueDrawer(issue, $event)">
                  <div class="card-top">
                    @if (selectionMode()) {
                      <mat-checkbox [checked]="isSelected(issue.id)"
                        (click)="$event.stopPropagation()"
                        (change)="toggleIssueSelection(issue.id)"></mat-checkbox>
                    }
                    <button type="button" class="key key-btn" (click)="openIssueDrawer(issue, $event)">{{ issue.key }}</button>
                    @if (issue.isOverdue) {
                      <span class="tt-badge tt-badge--overdue">Overdue</span>
                    }
                  </div>
                  <strong>{{ issue.summary }}</strong>
                  @if (issue.isOverdue) {
                    <div class="overdue-banner">{{ issue.delayLabel || ('Overdue by ' + (issue.delayInDays || 0) + ' days') }}</div>
                  } @else if (issue.deadlineBadge || issue.remainingLabel) {
                    <div class="remaining deadline-badge" [ngClass]="deadlineBadgeClass(issue.deadlineBadge, issue.isOverdue)">{{ issue.deadlineBadge || issueDeadlineLabel(issue) }}</div>
                  }
                  <div class="meta">
                    <span class="chip">{{ issue.typeLabel }}</span>
                    <span class="tt-badge" [ngClass]="priorityBadgeClass(issue.priority)">{{ issue.priorityLabel }}</span>
                    @if (issue.assigneeName) {
                      <span class="chip">{{ issue.assigneeName }}</span>
                    }
                    @if (issue.storyPoints != null) {
                      <span class="chip">{{ issue.storyPoints }} pts</span>
                    }
                  </div>
                  @if (issue.labels.length) {
                    <div class="meta">
                      @for (label of issue.labels; track label) {
                        <span class="chip label">{{ label }}</span>
                      }
                    </div>
                  }
                </div>
              } @empty {
                <p class="empty">Drop issues here</p>
              }
            </div>
          }
        </div>
      }
    </div>
  }
</div>
